import { hasConnectorIsBookable } from '../constants/integration-features';
import { ADDONS_ASSET_BASE } from './addon-logo.util';

export interface ConnectorPremiumConfig {
  catalogVisible?: boolean | string | null;
  bookingLimitExempt?: boolean;
  bookingTermsRequired?: boolean;
  bookingTermsUrl?: string | null;
  bookingTermsPrefix?: Record<string, string> | null;
  bookingTermsLink?: Record<string, string> | null;
  preBooking?: Record<string, unknown> | null;
}

export interface ConnectorBookingTerms {
  url: string;
  prefix: string;
  linkLabel: string;
}

export interface ConnectorPreBooking {
  formUrl?: string;
  formTitle?: string;
  formDescription?: string;
  formButton?: string;
  contactTitle?: string;
  contactDescription?: string;
  stepLabel?: string;
  continueLabel?: string;
}

function manifestLocalizedString(
  value: unknown,
  lang: string,
): string | undefined {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'object' && value !== null) {
    const obj = value as Record<string, string>;
    return obj[lang] ?? obj['en'] ?? undefined;
  }
  return undefined;
}

function resolveBookingTermsUrl(url: string): string {
  const trimmed = url.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  return `${ADDONS_ASSET_BASE}/${trimmed.replace(/^\/+/, '')}`;
}

function httpUrl(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.startsWith('http://') || trimmed.startsWith('https://') ? trimmed : undefined;
}

export function connectorPremiumConfig(
  manifest: Record<string, unknown> | undefined | null,
): ConnectorPremiumConfig {
  const premium = manifest?.['premium'];
  return premium && typeof premium === 'object' ? (premium as ConnectorPremiumConfig) : {};
}

export function connectorCatalogVisibleFlag(
  manifest: Record<string, unknown> | undefined | null,
): boolean | string | null | undefined {
  return connectorPremiumConfig(manifest).catalogVisible;
}

export function isConnectorCatalogVisible(
  manifest: Record<string, unknown> | undefined | null,
): boolean {
  const visible = connectorPremiumConfig(manifest).catalogVisible;
  if (visible === undefined || visible === null) {
    return true;
  }
  if (typeof visible === 'string') {
    return visible.toLowerCase() !== 'false';
  }
  return Boolean(visible);
}

export function isConnectorInAddonsCatalog(
  manifest: Record<string, unknown> | undefined | null,
): boolean {
  return isConnectorCatalogVisible(manifest);
}

export function isConnectorSysadminGrantOnly(
  manifest: Record<string, unknown> | undefined | null,
): boolean {
  return !isConnectorCatalogVisible(manifest);
}

export function isConnectorInFirmAddonSurface(
  manifest: Record<string, unknown> | undefined | null,
  connectorRid: string | undefined,
  lawFirmFeatures: readonly string[] | undefined,
): boolean {
  if (isConnectorInAddonsCatalog(manifest)) {
    return true;
  }
  return hasConnectorIsBookable(lawFirmFeatures, connectorRid);
}

export function connectorBookingLimitExempt(
  manifest: Record<string, unknown> | undefined | null,
): boolean {
  return connectorPremiumConfig(manifest).bookingLimitExempt === true;
}

/**
 * DMS / practice-management connectors (Kleos, Actaport, DocuWare, …).
 * Knowledge-base addons such as Otto Schmidt set premium.bookingLimitExempt and are not DMS.
 */
export function isDmsConnector(
  manifest: Record<string, unknown> | undefined | null,
): boolean {
  return !connectorBookingLimitExempt(manifest);
}

export function connectorBookingTerms(
  manifest: Record<string, unknown> | undefined | null,
  lang: string,
): ConnectorBookingTerms | null {
  const premium = connectorPremiumConfig(manifest);
  if (!premium.bookingTermsRequired) {
    return null;
  }
  const url = premium.bookingTermsUrl?.trim();
  const prefix = manifestLocalizedString(premium.bookingTermsPrefix, lang);
  const linkLabel = manifestLocalizedString(premium.bookingTermsLink, lang);
  if (!url || !prefix || !linkLabel) {
    return null;
  }
  return {
    url: resolveBookingTermsUrl(url),
    prefix,
    linkLabel,
  };
}

export function connectorPreBooking(
  manifest: Record<string, unknown> | undefined | null,
  lang: string,
): ConnectorPreBooking | null {
  const raw = connectorPremiumConfig(manifest).preBooking;
  if (!raw) {
    return null;
  }

  const formUrl = httpUrl(raw['formUrl']);
  const formTitle = manifestLocalizedString(raw['formTitle'], lang);
  const formDescription = manifestLocalizedString(raw['formDescription'], lang);
  const formButton = manifestLocalizedString(raw['formButton'], lang);
  const stepLabel = manifestLocalizedString(raw['stepLabel'], lang);
  const continueLabel = manifestLocalizedString(raw['continueLabel'], lang);
  const contactTitle = manifestLocalizedString(raw['contactTitle'], lang);
  const contactDescription = manifestLocalizedString(raw['contactDescription'], lang);

  const hasForm = Boolean(
    formUrl && formTitle && formDescription && stepLabel && continueLabel,
  );
  const hasContact = Boolean(contactTitle || contactDescription);
  if (!hasForm && !hasContact) {
    return null;
  }

  return {
    ...(hasForm
      ? {
          formUrl,
          formTitle,
          formDescription,
          ...(formButton ? { formButton } : {}),
          stepLabel,
          continueLabel,
        }
      : {}),
    contactTitle,
    contactDescription,
  };
}

export function formatPreBookingStepLabel(
  template: string,
  current: number,
  total: number,
): string {
  return template
    .replaceAll('{{current}}', String(current))
    .replaceAll('{{total}}', String(total));
}
